import CancellableHelper from "@/requests/CancellableHelper";

// Models
import { ConnectorSettings } from "@/models/api/ConnectorSettings";
import { Migrations } from "@/models/api/developer/Migrations";
import { MigrationScript } from "@/models/api/developer/MigrationScript";
import GenericTableRequest from "@/models/api/GenericTableRequest";
import { Cert } from "@/models/api/developer/Cert";

//#region Routes
export const URL_GET_CONNECTOR_SETTINGS = "/Developer/GetConnectorSettings";
export const URL_GET_MIGRATIONS = "/Developer/Migrations";
export const URL_GET_MIGRATION_SCRIPT = "/Developer/MigrationScript";

export const URL_POST_MIGRATE_ALL = "/Developer/Migrate";
export const URL_POST_MIGRATE_CURRENT = "/Developer/Migrate/Current";
export const URL_POST_TEST_CONNECTOR_METHOD = "/Developer/TestConnectorMethod";
export const URL_POST_REGISTER_USER = "/Developer/RegisterUser";
export const URL_POST_REGISTER_EXTERNAL_USER = "/Developer/RegisterExternalUser";
export const URL_POST_LOGIN_EXTERNAL_USER = "/Developer/LoginExternalUser";
export const URL_GET_ENCRYPT_PASSWORD = "/Developer/GetEncryptPassword";
export const URL_GET_CHECK_CONTRACTORS_SHORT_NAMES = "/Developer/CheckContractorsShortNames";
export const URL_GET_TEST_BUTTON1 = "/Developer/TestButton1";
export const URL_GET_TEST_BUTTON2 = "/Developer/TestButton2";
export const URL_GET_TEST_BUTTON3 = "/Developer/TestButton3";
export const URL_POST_ADD_EXPORT_ITEMS = "/Developer/AddExportItems";
export const URL_GET_FILES_AS_ZIP = "/Developer/DownloadFilesAsZipPackage";
export const URL_GET_CHECK_CARBON_FOOTPRINT_CONNECTION = "/Developer/CheckCarbonFootprintConnection";
export const URL_POST_RETRY_EXPORT_BANK_STATEMENT_TO_OPTIMA = "/Developer/RetryExportBankStatementToOptima";
export const URL_POST_RETRY_EXPORT_DOCUMENT_TO_OPTIMA = "/Developer/RetryExportDocumentToOptima";
export const URL_POST_ADD_KSEF_CERT = "/Developer/AddKsefCert";
export const URL_POST_GET_RSA_FROM_ECDSA = "/Developer/GetRSAFromECDsa";
//#endregion

const _getConnectorSettings = new CancellableHelper<ConnectorSettings>(URL_GET_CONNECTOR_SETTINGS, ConnectorSettings);
export const getConnectorSettings = async (): Promise<ConnectorSettings> => {
  return await _getConnectorSettings.get();
};

const _getMigrations = new CancellableHelper<Migrations>(URL_GET_MIGRATIONS, Migrations, true);
export const getMigrations = async (): Promise<Migrations> => {
  return await _getMigrations.get();
};

const _getMigrationScript = new CancellableHelper<MigrationScript>(URL_GET_MIGRATION_SCRIPT, MigrationScript, true);
export const getMigrationScript = async (current: string, previous: string): Promise<MigrationScript> => {
  return await _getMigrationScript.get({
    current,
    previous,
  });
};

const _postMigrateAll = new CancellableHelper<void>(URL_POST_MIGRATE_ALL);
export const postMigrateAll = async (): Promise<void> => {
  return await _postMigrateAll.post();
};

const _postMigrateCurrent = new CancellableHelper<void>(URL_POST_MIGRATE_CURRENT);
export const postMigrateCurrent = async (): Promise<void> => {
  return await _postMigrateCurrent.post();
};

const _postTestConnectorMethod = new CancellableHelper<void>(URL_POST_TEST_CONNECTOR_METHOD, undefined, true);
export const postTestConnectorMethod = async (methodName: string, param: string): Promise<void> => {
  return await _postTestConnectorMethod.post({ MethodName: methodName, Param: param }, null);
};

const _postRegisterUser = new CancellableHelper<void>(URL_POST_REGISTER_USER, undefined, true);
export const postRegisterUser = async (): Promise<void> => {
  return await _postRegisterUser.post();
};

const _postRegisterExternalUser = new CancellableHelper<void>(URL_POST_REGISTER_EXTERNAL_USER, undefined, true);
export const postRegisterExternalUser = async (login: string, password: string): Promise<void> => {
  return await _postRegisterExternalUser.post({ Login: login, Password: password }, null);
};

const _postLoginExternalUser = new CancellableHelper<void>(URL_POST_LOGIN_EXTERNAL_USER, undefined, true);
export const postLoginExternalUser = async (login: string, password: string): Promise<void> => {
  return await _postLoginExternalUser.post({ Login: login, Password: password }, null);
};

const _getEncryptPassword = new CancellableHelper<string>(URL_GET_ENCRYPT_PASSWORD, undefined);
export const getEncryptPassword = async (password: string): Promise<string> => {
  return await _getEncryptPassword.get({ password: password });
};

const _getCheckContractorShortNames = new CancellableHelper<void>(
  URL_GET_CHECK_CONTRACTORS_SHORT_NAMES,
  undefined,
  true
);
export const getCheckContractorShortNames = async (): Promise<void> => {
  return await _getCheckContractorShortNames.get();
};

const _postAddExportItems = new CancellableHelper<void>(URL_POST_ADD_EXPORT_ITEMS, null, true);
_postAddExportItems.jsonSerializer = true;
export const postAddExportItems = async (genericTableRequest: GenericTableRequest): Promise<void> => {
  return await _postAddExportItems.post(genericTableRequest, null);
};

const _testButton1 = new CancellableHelper<void>(URL_GET_TEST_BUTTON1, undefined, true);
export const testButton1 = async (testText: string): Promise<void> => {
  return await _testButton1.get({ testText: testText });
};

const _testButton2 = new CancellableHelper<void>(URL_GET_TEST_BUTTON2, undefined, true);
export const testButton2 = async (testText: string): Promise<void> => {
  return await _testButton2.get({ testText: testText });
};

const _testButton3 = new CancellableHelper<void>(URL_GET_TEST_BUTTON3, undefined, true);
export const testButton3 = async (testText: string): Promise<void> => {
  return await _testButton3.get({ testText: testText });
};

const _downloadFilesPackage = new CancellableHelper<File>(URL_GET_FILES_AS_ZIP);
_downloadFilesPackage.isFile = true;
export const downloadFilesPackage = async (fileIds: string): Promise<File> => {
  return await _downloadFilesPackage.get({
    fileIds: fileIds,
  });
};

const _checkCarbonFootprintConnection = new CancellableHelper<null>(URL_GET_CHECK_CARBON_FOOTPRINT_CONNECTION);
export const checkCarbonFootprintConnection = async () => {
  return _checkCarbonFootprintConnection.get();
};

const _retryExportDocumentToOptima = new CancellableHelper<null>(URL_POST_RETRY_EXPORT_DOCUMENT_TO_OPTIMA);
export const retryExportDocumentToOptima = async (documentNumber: string) => {
  return _retryExportDocumentToOptima.post({
    documentNumber: documentNumber,
  });
};

const _retryExportBankStatementToOptima = new CancellableHelper<null>(URL_POST_RETRY_EXPORT_BANK_STATEMENT_TO_OPTIMA);
export const retryExportBankStatementToOptima = async (bankStatementNumber: string) => {
  return _retryExportBankStatementToOptima.post({
    bankStatementNumber: bankStatementNumber,
  });
};

const _addKsefCert = new CancellableHelper<void>(URL_POST_ADD_KSEF_CERT);
export const addKsefCert = async (cert: Cert): Promise<void> => {
  return await _addKsefCert.post(cert, null);
};

const _getRSAFromECDsa = new CancellableHelper<void>(URL_POST_GET_RSA_FROM_ECDSA);
export const getRSAFromECDsa = async (cert: Cert): Promise<void> => {
  return await _getRSAFromECDsa.post(cert, null);
};
